/**
 * GlobalRagChat · Sol, asistente flotante de DenDev.
 *
 * Personaje: Sol — analista comercial femenina, voseo argentino, sin "che".
 *
 * Scope automático según la URL:
 *   - En `/prospects/:id` → prospect-scoped (RAG sobre docs del prospect,
 *     header muestra "Sol · {prospect}", threads guardan prospect_id).
 *   - En cualquier otra ruta → modo libre (RAG sobre todo lo accesible,
 *     threads sin prospect_id). Base para el agente global futuro.
 *
 * Features actuales:
 *   - FAB con monograma "S" sobre gradiente cyan→primary (más distintivo
 *     que el Sparkles genérico anterior).
 *   - Bottom-sheet en mobile, panel flotante en desktop.
 *   - Historial de conversaciones (dropdown en header) — click en un thread
 *     previo lo carga.
 *   - RAG search + citations.
 *   - Persistencia: threads + mensajes en core.rag_chat_*.
 *   - Reset automático al cambiar de scope.
 */
import {
    Loader2,
    MessageSquarePlus,
    Minus,
    Send,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useMatch } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useProspect } from '@/features/prospects/hooks';
import { AccionesDeAdjunto } from '@/features/sol/components/AccionesDeAdjunto';
import { AdjuntosDeImagen } from '@/features/sol/components/AdjuntosDeImagen';
import { useAdjuntosDeSol, type NotaDeVoz } from '@/features/sol/hooks/useAdjuntosDeSol';
import { useEnviarASol } from '@/features/sol/useEnviarASol';
import { cn } from '@/lib/utils';
import {
    archiveThread,
    getLatestThreadForProspect,
    listRecentThreads,
    listThreadMessages,
    type ChatMessage,
    type ChatThread,
} from '@/services/ragChatService';

import { HistorialDeSol } from './sol-chat/HistorialDeSol';
import { SolAvatar, SolFab } from './sol-chat/SolAvatar';
import { AssistantThinking, EmptyHint, MessageBubble } from './sol-chat/SolMessages';

export function GlobalRagChat() {
    const enviarASol = useEnviarASol();
    const [sending, setSending] = useState(false);
    const adj = useAdjuntosDeSol({ bloqueado: sending });
    const { imagenes } = adj;
    const matchProspect = useMatch({ path: '/prospects/:id/*', end: false });
    const prospectId = matchProspect?.params.id ?? null;
    const isRealProspectId = !!prospectId && prospectId.length >= 20;

    const { data: prospect } = useProspect(isRealProspectId ? prospectId! : undefined);

    const scopeKey = isRealProspectId ? prospectId : 'global';
    const scopeLabel = isRealProspectId
        ? prospect?.name ?? 'este prospect'
        : 'modo libre';
    const scopeSubtitle = isRealProspectId
        ? `Sol revisa los documentos cargados de ${prospect?.name ?? 'este prospect'}`
        : 'Sol busca en todos los documentos accesibles';

    const [open, setOpen] = useState(false);
    const [threadId, setThreadId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState<ChatThread[]>([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    // Reset al cambiar de scope. Incluye loading (sino quedaba colgado).
    useEffect(() => {
        setThreadId(null);
        setMessages([]);
        setLoading(false);
        setHistoryLoaded(false);
        setHistory([]);
    }, [scopeKey]);

    // Al abrir, cargar el thread más reciente del scope.
    //
    // BUG resuelto: antes el `finally` tenía `if (!cancelled) setLoading(false)`,
    // así que si el effect se desmontaba (race de re-render), el flag quedaba
    // colgado en `true` y la UI mostraba "Cargando…" infinito.
    // Fix: setLoading(false) SIEMPRE en finally. Si el componente se desmontó,
    // el setState es no-op (React lo ignora silenciosamente).
    // También sacamos `loading` de las deps — usamos `threadId` como guard.
    useEffect(() => {
        if (!open) return;
        if (threadId) return;
        if (!isRealProspectId) return;

        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const thread = await getLatestThreadForProspect(prospectId!);
                if (cancelled) return;
                if (thread) {
                    setThreadId(thread.id);
                    const msgs = await listThreadMessages(thread.id);
                    if (!cancelled) setMessages(msgs);
                }
            } catch (err) {
                console.warn('[GlobalRagChat] load thread falló:', err);
            } finally {
                setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, threadId, isRealProspectId, prospectId]);

    // Cargar el historial de threads previos para el dropdown.
    async function ensureHistoryLoaded() {
        if (historyLoaded) return;
        const list = isRealProspectId
            ? await listRecentThreads({ scope: 'prospect', prospectId: prospectId! }, 20)
            : await listRecentThreads({ scope: 'global' }, 20);
        setHistory(list);
        setHistoryLoaded(true);
    }

    // Autoscroll cuando llegan mensajes.
    useEffect(() => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, sending]);

    async function loadThread(id: string) {
        setLoading(true);
        try {
            setThreadId(id);
            const msgs = await listThreadMessages(id);
            setMessages(msgs);
        } finally {
            setLoading(false);
        }
    }

    async function handleSend(nota?: NotaDeVoz) {
        // Con nota de voz el texto escrito no viaja y tampoco se toca: queda en el cuadro.
        const question = nota ? '' : input.trim();
        if (sending) return;
        if (!nota && ((!question && imagenes.imagenes.length === 0) || imagenes.procesando)) {
            return;
        }
        setSending(true);
        let tempId: string | null = null;
        try {
            const prep = await adj.armar(nota);
            if (!prep) return;
            const { extra, citations, ids } = prep;
            tempId = `tmp-${Date.now()}`;
            const tempUserMsg: ChatMessage = {
                id: tempId,
                thread_id: threadId ?? 'pending',
                role: 'user',
                content: question,
                citations,
                model: null,
                error: null,
                created_at: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, tempUserMsg]);
            if (!nota) setInput('');

            const res = await enviarASol({
                thread_id: threadId ?? undefined,
                prospect_id: isRealProspectId ? prospectId! : undefined,
                question,
                // Siempre agente (Anexo F): tanto el chat global como el scopeado a
                // un prospecto usan el orquestador. Si hay prospect_id, el edge
                // pre-carga el RAG de sus documentos y se lo pasa al agente, que
                // conserva todas sus tools + reglas (copy en <pre>, tono, fecha).
                mode: 'agent',
                ...extra,
            });
            if (!res.ok) {
                adj.avisarFallo(nota, 'Sol tuvo un problema', res.error);
                setMessages((prev) => prev.filter((m) => m.id !== tempId));
                return;
            }
            // Solo salen las imágenes que viajaron.
            imagenes.limpiar(ids);
            if (!threadId) setThreadId(res.thread_id);
            const fresh = await listThreadMessages(res.thread_id);
            setMessages(fresh);
            // Invalidar historial para que el dropdown vea el nuevo título cuando se abra.
            setHistoryLoaded(false);
        } catch (err) {
            adj.avisarFallo(
                nota,
                'Error de red',
                err instanceof Error ? err.message : 'desconocido',
            );
            if (tempId) setMessages((prev) => prev.filter((m) => m.id !== tempId));
        } finally {
            setSending(false);
            adj.terminar();
        }
    }

    async function handleNewThread() {
        if (threadId) await archiveThread(threadId);
        setThreadId(null);
        setMessages([]);
        setHistoryLoaded(false);
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSend();
        }
    }

    if (!open) {
        return <SolFab onClick={() => setOpen(true)} scopeLabel={scopeLabel} />;
    }

    return (
        <Card
            className="bg-bg-elevated border-border fixed bottom-0 left-0 right-0 z-30 flex h-[75vh] max-h-[75vh] w-full flex-col gap-0 rounded-b-none p-0 shadow-2xl sm:bottom-5 sm:left-auto sm:right-5 sm:h-auto sm:max-h-[min(640px,calc(100vh-2.5rem))] sm:w-[min(440px,calc(100vw-2.5rem))] sm:rounded-b-xl"
        >
            {/* Header con avatar de Sol */}
            <div className="border-border flex items-center gap-2.5 border-b px-3 py-2.5">
                <SolAvatar size="sm" />
                <div className="min-w-0 flex-1">
                    <div className="text-foreground inline-flex items-center gap-1.5 text-body font-semibold leading-tight">
                        Sol
                        <span className="text-muted-foreground/60 text-micro font-normal">
                            · {scopeLabel}
                        </span>
                    </div>
                    <p className="text-muted-foreground truncate text-micro leading-tight">
                        {scopeSubtitle}
                    </p>
                </div>

                <HistorialDeSol
                    history={history}
                    historyLoaded={historyLoaded}
                    threadId={threadId}
                    onOpen={() => void ensureHistoryLoaded()}
                    onSelect={(id) => void loadThread(id)}
                />

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleNewThread}
                    title="Nueva conversación con Sol"
                    className="h-9 w-9 shrink-0 sm:h-7 sm:w-7"
                >
                    <MessageSquarePlus className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setOpen(false)}
                    title="Minimizar"
                    className="h-9 w-9 shrink-0 sm:h-7 sm:w-7"
                >
                    <Minus className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                </Button>
            </div>

            {/* Messages */}
            <div
                ref={scrollRef}
                className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-3.5 py-3"
            >
                {loading && messages.length === 0 && (
                    <p className="text-muted-foreground text-center text-caption">
                        <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />
                        Cargando conversación…
                    </p>
                )}
                {!loading && messages.length === 0 && (
                    <EmptyHint isProspect={isRealProspectId} prospectName={prospect?.name} />
                )}
                {messages.map((m) => (
                    <MessageBubble key={m.id} message={m} />
                ))}
                {sending && <AssistantThinking texto={adj.textoDeCarga} />}
            </div>

            {/* Input */}
            <div
                className={cn('border-border border-t p-2', imagenes.arrastrando && 'bg-primary/5')}
                {...imagenes.propsDeArrastre}
            >
                <div className="bg-surface-lo border-border-dim flex flex-col rounded-md border p-1.5">
                    <AdjuntosDeImagen
                        imagenes={imagenes.imagenes}
                        onQuitar={imagenes.quitar}
                        disabled={sending}
                    />
                    <div className="flex items-stretch gap-1.5">
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={imagenes.alPegar}
                        placeholder={
                            isRealProspectId
                                ? `Preguntale a Sol sobre ${prospect?.name ?? 'este prospect'}…`
                                : 'Preguntale a Sol cualquier cosa de prospects o docs…'
                        }
                        rows={2}
                        className="text-foreground placeholder:text-muted-foreground/60 max-h-32 min-h-[44px] flex-1 resize-none bg-transparent px-1.5 py-1 leading-snug outline-none"
                        style={{
                            // iOS auto-zoomea cuando font-size < 16px en focus. Forzamos
                            // 16px en mobile y reducimos en desktop con CSS.
                            fontSize: '16px',
                        }}
                        disabled={sending}
                    />
                    {/* Botón proporcional al alto del textarea (items-stretch en el
                        contenedor) — alto se ajusta solo, ancho fijo cuadrado. */}
                    <Button
                        onClick={() => void handleSend()}
                        disabled={
                            (!input.trim() && imagenes.imagenes.length === 0) ||
                            sending ||
                            imagenes.procesando
                        }
                        className="h-auto w-11 shrink-0 self-stretch p-0 sm:w-10"
                        title="Enviar (Enter)"
                    >
                        {sending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" />
                        )}
                    </Button>
                    </div>
                    <AccionesDeAdjunto
                        onArchivos={(f) => void imagenes.agregar(f)}
                        onNotaDeVoz={(blob, duracionMs) => void handleSend({ blob, duracionMs })}
                        disabled={sending}
                    />
                </div>
                <p className="text-muted-foreground mt-1 text-center text-micro">
                    Enter para enviar · Shift+Enter para salto de línea
                </p>
            </div>
        </Card>
    );
}
